import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CalendarPlus, Loader2, Send } from 'lucide-react';
import { localInputToUtcIso } from '../lib/calendarDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DURATIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1h' },
  { value: 90, label: '1h30' },
  { value: 120, label: '2h' },
];

// Brief Jul 7 2026 §4 — modal "Demander une session 1-to-1" (apprenant → expert).
const SessionRequestModal = ({ isOpen, onClose, expert }) => {
  const [subject, setSubject] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const submit = async () => {
    if (!subject.trim() || subject.trim().length < 3) {
      toast.error('Indique le sujet de la session (min. 3 caractères).');
      return;
    }
    if (!date || !time) {
      toast.error('Choisis une date et une heure souhaitées.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/session-requests`, {
        expert_id: expert.user_id,
        subject: subject.trim(),
        requested_date: localInputToUtcIso(`${date}T${time}`),
        duration_minutes: duration,
        message: message.trim(),
      }, { withCredentials: true });
      toast.success(`Demande envoyée à ${expert.display_name || "l'expert"} !`);
      setSubject(''); setDate(''); setTime(''); setMessage('');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="session-request-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-[#7F77DD]" /> Demander une session 1-to-1
          </h2>
          <button type="button" onClick={onClose} data-testid="session-request-close"
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          À : <span className="font-medium text-gray-800 dark:text-gray-200">{expert.display_name || 'Expert'}</span>
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Sujet de la session</label>
            <input
              type="text"
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex : Coaching gestion de fortune"
              data-testid="session-request-subject"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#7F77DD] outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Date souhaitée</label>
              <input
                type="date"
                value={date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                data-testid="session-request-date"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#7F77DD] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Heure souhaitée</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="session-request-time"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#7F77DD] outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Durée souhaitée</label>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDuration(d.value)}
                  data-testid={`session-request-duration-${d.value}`}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    duration === d.value
                      ? 'bg-[#7F77DD] text-white border-[#7F77DD]'
                      : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-[#7F77DD]'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Message (optionnel)</label>
            <textarea
              rows={3}
              maxLength={1000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Précise ton besoin, ton contexte…"
              data-testid="session-request-message"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#7F77DD] outline-none resize-none"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={sending}
          data-testid="session-request-submit"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#7F77DD] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Envoyer la demande →
        </button>
      </div>
    </div>
  );
};

export default SessionRequestModal;
